import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import {
    Alert,
    Card,
    CardContent,
    Collapse,
    Typography,
    TableHead,
    Avatar,
    Box,
    Table,
    TableBody,
    TableCell,
    TablePagination,
    TableRow,
    TableFooter,
    IconButton
} from "@mui/material";
import FeatherIcon from "feather-icons-react";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

import Breadcrumb from "../../../layouts-v2/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components-v2/container/PageContainer";

import img4 from "../../../assets-v2/images/users/4.jpg";

import OrganizationUserService from "../../../services-v2/eitri-manager-api/OrganizationUserService";
import { Link } from "react-router-dom";
import useAlert from "../../../hooks/useAlert";
import useStatus from "../../../hooks/useStatus";

import Loading from "../../../layouts-v2/loading/Loading";

function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = event => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = event => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = event => {
        console.log("apertei o button");
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = event => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === "rtl" ? (
                    <LastPageIcon />
                ) : (
                    <FirstPageIcon />
                )}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === "rtl" ? (
                    <KeyboardArrowRight />
                ) : (
                    <KeyboardArrowLeft />
                )}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === "rtl" ? (
                    <KeyboardArrowLeft />
                ) : (
                    <KeyboardArrowRight />
                )}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === "rtl" ? (
                    <FirstPageIcon />
                ) : (
                    <LastPageIcon />
                )}
            </IconButton>
        </Box>
    );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired
};

const BCrumb = [
    {
        title: "Usuários"
    }
];

const UserList = () => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [users, setUsers] = React.useState([]);
    const [totalUsers, setTotalUsers] = React.useState(1);
    const { alertInfo, setAllAlertInfo, setInitialAlertInfo } = useAlert();
    const { status, setAllStatus } = useStatus();

    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows =
    //     page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

    React.useEffect(() => {
        fetchUsers(rowsPerPage, page);
    }, []);

    async function fetchUsers(usersPerPage, page) {
        try {
            setAllStatus({ isFetching: true, success: false, error: false });
            const response = await OrganizationUserService.findUsersByOrganizationId(
                usersPerPage,
                page
            );
            setUsers(response.data);
            setTotalUsers(response.totalItems);
            setAllStatus({ isFetching: false, success: true, error: false });
        } catch (err) {
            setAllStatus({ isFetching: false, success: false, error: true });
            setAllAlertInfo({
                theme: "error",
                message: `Ops! Não foi possível carregar os usuários.`,
                visible: true
            });
            console.error(err);
        }
    }

    const handleChangePage = (event, newPage) => {
        const pageToLoadFromService = newPage + 1;
        setPage(newPage);
        fetchUsers(rowsPerPage, pageToLoadFromService);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        fetchUsers(event.target.value, 1);
        setPage(0);
    };

    return (
        <>
            {status.isFetching ? (
                <Loading isVisible={status.isFetching} />
            ) : (
                <PageContainer
                    title={"Usuários"}
                    description="this is Pagination Table page"
                >
                    {/* breadcrumb */}
                    <Breadcrumb items={BCrumb} />
                    {/* end breadcrumb */}
                    <Card>
                        <Collapse in={alertInfo.visible}>
                            <Alert
                                variant="filled"
                                severity={alertInfo.theme || "info"}
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            setInitialAlertInfo();
                                        }}
                                    >
                                        <FeatherIcon icon="x" width="20" />
                                    </IconButton>
                                }
                            >
                                {alertInfo.message}
                            </Alert>
                        </Collapse>
                        <CardContent>
                            <Box
                                sx={{
                                    overflow: {
                                        xs: "auto",
                                        md: "unset"
                                    }
                                }}
                            >
                                <Table
                                    aria-label="custom pagination table"
                                    sx={{
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant="h5">
                                                    Usuário
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="h5">
                                                    Role
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user, id) => (
                                            <TableRow key={id}>
                                                <TableCell>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        <Avatar
                                                            src={img4}
                                                            alt={img4}
                                                            width="30"
                                                            sx={{
                                                                borderRadius:
                                                                    "100%"
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                ml: 2
                                                            }}
                                                            style={{
                                                                cursor:
                                                                    "pointer"
                                                            }}
                                                        >
                                                            <Link
                                                                to={{
                                                                    pathname: `/user-management/users/${user.email}`
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    fontWeight="600"
                                                                >
                                                                    {user.email}
                                                                </Typography>
                                                            </Link>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        color="textSecondary"
                                                        variant="h6"
                                                        fontWeight="400"
                                                    >
                                                        {user.role}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* 
                                        {emptyRows > 0 && (
                                            <TableRow
                                                style={{ height: 53 * emptyRows }}
                                            >
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )} */}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TablePagination
                                                rowsPerPageOptions={[5, 10, 25]}
                                                colSpan={6}
                                                count={totalUsers}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                labelRowsPerPage={
                                                    "Itens por página:"
                                                }
                                                SelectProps={{
                                                    inputprops: {
                                                        "aria-label":
                                                            "usuários por página"
                                                    },
                                                    native: true
                                                }}
                                                onPageChange={handleChangePage}
                                                onRowsPerPageChange={
                                                    handleChangeRowsPerPage
                                                }
                                                ActionsComponent={
                                                    TablePaginationActions
                                                }
                                            />
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </Box>
                        </CardContent>
                    </Card>
                </PageContainer>
            )}
        </>
    );
};

export default UserList;
